import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getVisitAnalytics, getVisits, exportVisits, getVisitorStats } from '../../services/visitTrackingService';
import { RingLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { 
  FaEye, 
  FaUsers, 
  FaCalendarWeek, 
  FaChartLine, 
  FaFilter,
  FaDownload,
  FaHome,
  FaUserPlus,
  FaBoxOpen,
  FaUserCircle,
  FaGlobe
} from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SiteVisitsAnalytics = () => {
  const { t } = useTranslation();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('visits'); // 'visits' or 'visitors'
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Visits table state
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Visitor stats state
  const [visitorStats, setVisitorStats] = useState([]);
  const [visitorStatsLoading, setVisitorStatsLoading] = useState(false);
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorSortBy, setVisitorSortBy] = useState('visitCount'); // 'visitCount', 'lastVisit', 'country'
  const [visitorSortDesc, setVisitorSortDesc] = useState(true);
  
  // Filter state
  const [filters, setFilters] = useState({
    pageType: '',
    pageId: '',
    trafficType: '',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  // Fetch analytics data
  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Fetch visits when page or filters change
  useEffect(() => {
    if (activeTab === 'visits') {
      fetchVisits();
    } else {
      fetchVisitorStats();
    }
  }, [currentPage, appliedFilters, activeTab]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getVisitAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVisits = async () => {
    try {
      setVisitsLoading(true);
      const filterParams = {};
      
      if (appliedFilters.pageType) filterParams.pageType = appliedFilters.pageType;
      if (appliedFilters.pageId) filterParams.pageId = appliedFilters.pageId;
      if (appliedFilters.trafficType) filterParams.trafficType = appliedFilters.trafficType;
      if (appliedFilters.startDate) filterParams.startDate = new Date(appliedFilters.startDate).getTime();
      if (appliedFilters.endDate) filterParams.endDate = new Date(appliedFilters.endDate + 'T23:59:59').getTime();
      
      const response = await getVisits(filterParams, currentPage, 50);
      setVisits(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Failed to fetch visits:', error);
      toast.error('Failed to load visits');
    } finally {
      setVisitsLoading(false);
    }
  };

  const fetchVisitorStats = async () => {
    try {
      setVisitorStatsLoading(true);
      const data = await getVisitorStats();
      setVisitorStats(data || []);
    } catch (error) {
      console.error('Failed to fetch visitor stats:', error);
      toast.error('Failed to load visitor statistics');
    } finally {
      setVisitorStatsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilters({
      pageType: '',
      pageId: '',
      trafficType: '',
      startDate: '',
      endDate: ''
    });
    setAppliedFilters({});
    setCurrentPage(0);
  };

  const handleExport = async () => {
    try {
      const filterParams = {};
      if (appliedFilters.pageType) filterParams.pageType = appliedFilters.pageType;
      if (appliedFilters.pageId) filterParams.pageId = appliedFilters.pageId;
      if (appliedFilters.trafficType) filterParams.trafficType = appliedFilters.trafficType;
      if (appliedFilters.startDate) filterParams.startDate = new Date(appliedFilters.startDate).getTime();
      if (appliedFilters.endDate) filterParams.endDate = new Date(appliedFilters.endDate + 'T23:59:59').getTime();
      
      const data = await exportVisits(filterParams);
      
      // Convert to CSV
      const csv = convertToCSV(data);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site-visits-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Visits exported successfully');
    } catch (error) {
      console.error('Failed to export visits:', error);
      toast.error('Failed to export visits');
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = ['ID', 'Page Type', 'Page ID', 'Visitor IP', 'Country', 'Location', 'Device', 'Traffic', 'Bot Name', 'User Email', 'Session ID', 'Referrer', 'Visited At'];
    const rows = data.map(visit => [
      visit.id,
      visit.pageType,
      visit.pageId || '',
      visit.visitorIp || '',
      visit.countryCode || '',
      visit.location || '',
      visit.deviceName || '',
      visit.bot ? 'Bot' : 'Human',
      visit.botName || '',
      visit.userEmail || 'Anonymous',
      visit.sessionId || '',
      visit.referrer || '',
      new Date(visit.visitedAt).toLocaleString()
    ]);
    
    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  };

  const getPageIcon = (pageType) => {
    switch (pageType) {
      case 'HOME': return <FaHome className="text-blue-500" />;
      case 'REGISTER': return <FaUserPlus className="text-green-500" />;
      case 'PRODUCT': return <FaBoxOpen className="text-purple-500" />;
      case 'CHECKOUT': return <FaBoxOpen className="text-amber-500" />;
      case 'PROFILE': return <FaUserCircle className="text-orange-500" />;
      default: return <FaEye className="text-gray-500" />;
    }
  };

  const countryFlag = (code) => {
    if (!code || !/^[A-Z]{2}$/i.test(code)) return '🌐';
    return String.fromCodePoint(...code.toUpperCase().split('').map(char => 127397 + char.charCodeAt()));
  };

  const getReferrerSource = (referrer) => {
    if (!referrer) return { source: 'Direct', full: '' };
    try {
      const url = new URL(referrer);
      return {
        source: url.hostname.replace(/^www\./, ''),
        full: referrer
      };
    } catch {
      return { source: referrer, full: referrer };
    }
  };

  // Get filtered and sorted visitor stats
  const getFilteredVisitorStats = () => {
    let filtered = visitorStats;
    
    // Apply search filter
    if (visitorSearch) {
      const searchLower = visitorSearch.toLowerCase();
      filtered = filtered.filter(stat => 
        stat.visitorIp.toLowerCase().includes(searchLower) ||
        (stat.country && stat.country.toLowerCase().includes(searchLower)) ||
        (stat.countryCode && stat.countryCode.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      if (visitorSortBy === 'visitCount') {
        aVal = a.visitCount;
        bVal = b.visitCount;
      } else if (visitorSortBy === 'lastVisit') {
        aVal = new Date(a.lastVisit).getTime();
        bVal = new Date(b.lastVisit).getTime();
      } else if (visitorSortBy === 'country') {
        aVal = a.country || '';
        bVal = b.country || '';
      } else {
        return 0;
      }
      
      if (visitorSortDesc) {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });
    
    return filtered;
  };

  const toggleSort = (column) => {
    if (visitorSortBy === column) {
      setVisitorSortDesc(!visitorSortDesc);
    } else {
      setVisitorSortBy(column);
      setVisitorSortDesc(true);
    }
  };

  // Chart data for last 7 days
  const chartData = analytics ? {
    labels: analytics.last7Days.map(day => {
      const date = new Date(day.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Unique Visitors',
        data: analytics.last7Days.map(day => day.uniqueVisitors),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4
      },
      {
        label: 'Human Visits',
        data: analytics.last7Days.map(day => day.totalVisits),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4
      },
      {
        label: 'Visits incl. bots',
        data: analytics.last7Days.map(day => day.totalVisitsWithBots),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        borderDash: [6, 4],
        tension: 0.4
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Last 7 Days Traffic'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RingLoader color="#3b82f6" size={60} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Site Visits Analytics</h1>
        <p className="text-gray-600">Monitor and analyze website traffic</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Today · People only</p>
              <p className="text-3xl font-bold text-gray-900">{analytics?.todayVisits || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics?.todayUniqueVisitors || 0} unique visitors
              </p>
            </div>
            <FaEye className="text-4xl text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Today · Including bots</p>
              <p className="text-3xl font-bold text-gray-900">{analytics?.todayVisitsWithBots || 0}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics?.todayUniqueVisitorsWithBots || 0} unique visitors
              </p>
            </div>
            <FaCalendarWeek className="text-4xl text-green-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Today · Automated traffic</p>
              <p className="text-3xl font-bold text-gray-900">{analytics?.todayBotVisits || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{analytics?.todaySpiderVisits || 0} Google Spiders</p>
            </div>
            <FaChartLine className="text-4xl text-purple-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">This week · People only</p>
              <p className="text-3xl font-bold text-gray-900">{analytics?.thisWeekVisits || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{analytics?.thisWeekUniqueVisitors || 0} unique visitors</p>
            </div>
            <FaUsers className="text-4xl text-orange-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Page Type Breakdown */}
      {analytics?.pageTypeBreakdown && analytics.pageTypeBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Page Type Breakdown (Last 7 Days)</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analytics.pageTypeBreakdown.map(stat => (
              <div key={stat.pageType} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl">{getPageIcon(stat.pageType)}</div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{stat.pageType}</p>
                  <p className="text-lg font-bold text-gray-700">{stat.count} <span className="text-xs font-normal text-gray-500">people</span></p>
                  <p className="text-xs text-gray-500">{stat.countWithBots} incl. bots · {stat.botCount} bots</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Traffic Trend</h2>
        <div style={{ height: '300px' }}>
          {chartData && <Line data={chartData} options={chartOptions} />}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('visits')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'visits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaEye className="inline mr-2" />
              Visit Logs
            </button>
            <button
              onClick={() => setActiveTab('visitors')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'visitors'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaGlobe className="inline mr-2" />
              Unique Visitors by Country
            </button>
          </nav>
        </div>
      </div>

      {/* Visit Logs Tab */}
      {activeTab === 'visits' && (
        <>
          {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FaFilter className="mr-2" />
            Filters
          </h2>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Page Type</label>
            <select
              name="pageType"
              value={filters.pageType}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="HOME">Home</option>
              <option value="REGISTER">Register</option>
              <option value="PRODUCT">Product</option>
              <option value="CHECKOUT">Checkout</option>
              <option value="PROFILE">Profile</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Traffic</label>
            <select
              name="trafficType"
              value={filters.trafficType}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">People + bots</option>
              <option value="HUMANS">People only</option>
              <option value="BOTS">Bots only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Page ID</label>
            <input
              type="text"
              name="pageId"
              value={filters.pageId}
              onChange={handleFilterChange}
              placeholder="e.g., product123"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex space-x-3 mt-4">
          <button
            onClick={applyFilters}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Visits Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Visit Logs ({totalElements} total)
          </h2>
        </div>

        {visitsLoading ? (
          <div className="flex justify-center items-center py-12">
            <RingLoader color="#3b82f6" size={40} />
          </div>
        ) : visits.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No visits found matching your filters
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Page ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visitor / Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Traffic
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referrer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visited At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visits.map(visit => (
                    <tr key={visit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {visit.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getPageIcon(visit.pageType)}
                          <span className="text-sm font-medium text-gray-900">{visit.pageType}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {visit.pageId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{countryFlag(visit.countryCode)} {visit.location || 'Unknown'}</div>
                        <div className="font-mono text-xs text-gray-400">{visit.visitorIp || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        <span className="font-sans">{visit.deviceName || 'Unknown'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {visit.bot ? (
                          <span className="rounded-full bg-purple-100 px-2 py-1 font-semibold text-purple-800">🤖 {visit.botName || 'Bot'}</span>
                        ) : (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 font-semibold text-emerald-800">Person</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <span
                          className={visit.referrer ? 'font-medium text-gray-700' : 'italic text-gray-400'}
                          title={getReferrerSource(visit.referrer).full || 'No referrer was supplied'}
                        >
                          {getReferrerSource(visit.referrer).source}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(visit.visitedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {currentPage + 1} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}

      {/* Unique Visitors Tab */}
      {activeTab === 'visitors' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FaGlobe className="mr-2" />
              Unique Visitors by Country ({visitorStats.length} unique IPs)
            </h2>
            <input
              type="text"
              placeholder="Search by IP or country..."
              value={visitorSearch}
              onChange={(e) => setVisitorSearch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {visitorStatsLoading ? (
            <div className="flex justify-center items-center py-12">
              <RingLoader color="#3b82f6" size={40} />
            </div>
          ) : getFilteredVisitorStats().length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {visitorSearch ? 'No visitors found matching your search' : 'No visitor data available'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('country')}
                    >
                      <div className="flex items-center">
                        Country
                        {visitorSortBy === 'country' && (
                          <span className="ml-1">{visitorSortDesc ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('visitCount')}
                    >
                      <div className="flex items-center">
                        Visit Count
                        {visitorSortBy === 'visitCount' && (
                          <span className="ml-1">{visitorSortDesc ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('lastVisit')}
                    >
                      <div className="flex items-center">
                        Last Visit
                        {visitorSortBy === 'lastVisit' && (
                          <span className="ml-1">{visitorSortDesc ? '↓' : '↑'}</span>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredVisitorStats().map(stat => (
                    <tr key={stat.visitorIp} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {stat.visitorIp}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {stat.country ? (
                          <div className="flex items-center space-x-2">
                            <span aria-hidden="true">{countryFlag(stat.countryCode)}</span>
                            <span>{stat.country}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Unknown</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                          {stat.visitCount}
                        </span>
                        <div className="mt-1 text-xs text-gray-500">{stat.deviceName || 'Unknown device'}</div>
                        {stat.bot && <div className="mt-1 text-xs font-semibold text-purple-700">🤖 {stat.botName || 'Bot'}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(stat.lastVisit).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteVisitsAnalytics;
